import { useState, memo, useContext } from 'react'

// Material UI
import {
  InputLabel,
  Stack,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  useTheme,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Divider,
  Switch
} from '@mui/material'

import { Close } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'

// form validation
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { createRubro } from '../services/rubros.operaciones'
import SnackBarUtils from '../../../../utils/SnackBarUtils'
import Loading from '../../../../components/Common/Loading'
import DesplegableRubrosPadres from '../components/desplegableRubrosPadres'
import { HuePicker } from 'react-color'
import KeywordsSelect from './keywordsWidget'
import { TenantsContext } from '../../../../context/tenants.context'
import UploadImagen from './uploadImg'
import CheckboxGroup from './CheckBox'
import { GruposRubros } from '../../../Integraciones/Importacion/types/atributos'
import {
  AtributosPermitidos,
  GruposAtributos,
  MonedaList,
  UnidadDeMedidaList
} from '../../../Integraciones/types/responses'
import { Rubro, RubroPost } from '../types/rubro'
import AtributosRubro from './Atributos'

const MAX_NOMBRE_LENGTH = 85
const MAX_NOMBRE_FULL_LENGTH = 150
const MAX_COLOR_LENGTH = 6
const MAX_SLUG_LENGTH = 45

export interface FormRubrosProps {
  open: boolean
  setOpen: (op: boolean) => void
  rubros: Rubro[]
  monedas: MonedaList[]
  unidadesDeMedida: UnidadDeMedidaList[]
  gruposAtr: GruposAtributos[]
  updateRubros: () => void
}

const CreaRubro = (props: FormRubrosProps) => {
  const { open, setOpen, rubros, monedas, unidadesDeMedida, gruposAtr, updateRubros } = props
  const [loading, setLoading] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [nombreRubro, setNombreRubro] = useState<string>('')
  const [nombreCompleto, setNombreCompleto] = useState<string>('')
  const [nombreBuscador, setNombreBuscador] = useState<string>('')
  const [slug, setSlug] = useState<string>('')
  const [rubroPadre, setRubroPadre] = useState<string>(null)
  const [color, setColor] = useState<string>('')
  const [unidadesSeleccionadas, setUnidadesSeleccionadas] = useState<any>([])
  const [gruposSeleccionados, setGruposSeleccionados] = useState<any>([])
  const [flagsConfig, setFlagsConfig] = useState<
    Record<string, { obligatorio: boolean; puedeCargarValorLibre: boolean }>
  >({})
  const [monedasSeleccionadas, setMonedasSeleccionadas] = useState<any>([])
  const [listable, setListable] = useState<boolean>(true)
  const [keywords, setKeywords] = useState<string>('')
  const [icono, setIcono] = useState<string>(null)
  const [foto, setFoto] = useState<string>(null)
  const [portada, setPortada] = useState<string>(null)
  const { tenant } = useContext(TenantsContext)

  const handleUnidadSeleccionadaChange = (unidadId: number) => {
    if (unidadesSeleccionadas.some((unidad: any) => unidad.idUnidadDeMedida === unidadId)) {
      setUnidadesSeleccionadas(unidadesSeleccionadas.filter((unidad: any) => unidad.idUnidadDeMedida !== unidadId))
    } else {
      setUnidadesSeleccionadas([...unidadesSeleccionadas, { idUnidadDeMedida: unidadId }])
    }
  }

  // Arma atributosPermitidos por atributo: flags + grupos seleccionados. Incluye el atributo si tiene
  // grupos, o si admite valor libre, o si es obligatorio (un atributo puede ir sin grupos = solo valor libre).
  const buildAtributosPermitidos = (): AtributosPermitidos[] =>
    gruposAtr
      .map((g: GruposRubros) => {
        const grupos = g.grupos.map((x) => x.idGrupo).filter((id: number) => gruposSeleccionados.includes(id))
        const flags = flagsConfig[g.slug] || { obligatorio: false, puedeCargarValorLibre: false }
        return {
          slug: g.slug,
          obligatorio: flags.obligatorio,
          puedeCargarValorLibre: flags.puedeCargarValorLibre,
          gruposPermitidos: grupos
        }
      })
      .filter((a) => a.gruposPermitidos.length > 0 || a.puedeCargarValorLibre || a.obligatorio)

  const handleMonedaSeleccionada = (monedaSelect: any) => {
    if (monedasSeleccionadas.some((moneda: any) => moneda.isoCode === monedaSelect.isoCode)) {
      setMonedasSeleccionadas(monedasSeleccionadas.filter((moneda: any) => moneda.isoCode !== monedaSelect.isoCode))
    } else {
      setMonedasSeleccionadas([...monedasSeleccionadas, monedaSelect])
    }
  }

  const schema: yup.AnyObjectSchema = yup.object({
    rubroName: yup.string().required('El Nombre es obligatorio'),
    minPrice: yup
      .number()
      .test('minPrice', 'El precio debe ser positivo y mayor a cero', function (value) {
        if (!value) {
          return true // No hay error si el campo está vacío
        }
        return yup.number().positive().isValidSync(value)
      })
      .transform((_, value) => {
        if (value.includes('.')) {
          return null
        }
        return +value.replace(/,/, '.')
      }),
    orden: yup
      .number()
      .required('Es obligatorio declarar el orden del rubro')
      .min(1, 'El orden debe ser mayor o igual a 1')
      .typeError('El orden debe ser un número'),
    colorRubro: yup.string().max(MAX_COLOR_LENGTH),
    nombreCompleto: yup.string().max(MAX_NOMBRE_FULL_LENGTH),
    slug: yup.string().max(MAX_SLUG_LENGTH)
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur'
  })

  const onSubmit = async (data: any) => {
    const rubro: RubroPost = {
      activo: true,
      listable: listable,
      codRubroPadre: rubroPadre,
      monedaList: monedasSeleccionadas,
      nombre: data.rubroName,
      nombreCompleto: data.nombreCompleto,
      orden: data.orden,
      precioMinimo: data.minPrice,
      atributosPermitidos: buildAtributosPermitidos(),
      unidadDeMedidaList: unidadesSeleccionadas,
      color: color,
      keywords: keywords,
      slug: data.slug,
      buscadorImagenNombreCaption: nombreBuscador,
      iconoBase64: icono ? icono.split(',')[1] : null,
      fotoBase64: foto ? foto.split(',')[1] : null,
      portadaBase64: portada ? portada.split(',')[1] : null
    }

    if (rubro.unidadDeMedidaList.length === 0) {
      return SnackBarUtils.error('Debes elegir por lo menos una unidad de medida')
    }

    if (rubro.monedaList.length === 0) {
      return SnackBarUtils.error('Debes elegir por lo menos un valor de moneda')
    }

    try {
      setLoading(true)
      await createRubro(rubro, tenant)
      updateRubros()
      SnackBarUtils.success('Rubro creado con éxito')
    } catch (e) {
      console.log('Error: ', e)
      SnackBarUtils.error('Ha ocurrido un error al crear el rubro')
    } finally {
      handleClose()
      setLoading(false)
    }
  }

  const handleClose = async () => {
    setOpen(false)
    setNombreRubro('')
    setSlug('')
    setUnidadesSeleccionadas([])
    setGruposSeleccionados([])
    setFlagsConfig({})
    setMonedasSeleccionadas([])
    setColor('')
    setIcono(null)
    setFoto(null)
    setPortada(null)
    reset()
  }

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.75)',
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography variant="h5">Grabando Rubro</Typography>
        <Loading color="inherit" />
      </Box>
    )
  }
  return (
    <>
      <Dialog
        maxWidth={isMobile ? 'lg' : 'md'}
        fullWidth
        fullScreen={isMobile}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            overflowY: 'auto',
            overflowX: 'hidden'
          }
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            Agregar un nuevo rubro
            {open ? (
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  color: (theme) => theme.palette.grey[500]
                }}
              >
                <Close />
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent>
            <Stack direction={isMobile ? 'column' : 'row'} sx={{ margin: '20px 0' }} spacing="20px">
              <Stack sx={{ width: '100%' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Título del rubro
                  <Typography variant="subtitle2">
                    {nombreRubro.length || 0}/{MAX_NOMBRE_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  disabled={loading}
                  sx={{ width: '100%' }}
                  error={!!errors.rubroName}
                  helperText={errors?.rubroName && String(errors?.rubroName?.message)}
                  variant="outlined"
                  {...register('rubroName')}
                  onChange={(e) => setNombreRubro(e.target.value)}
                  inputProps={{ maxLength: MAX_NOMBRE_LENGTH }}
                />
              </Stack>
            </Stack>

            <Stack direction={isMobile ? 'column' : 'row'} sx={{ margin: '20px 0', flexGrow: 1 }} spacing="20px">
              <Stack sx={{ width: '100%' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Nombre completo
                  <Typography variant="subtitle2">
                    {nombreCompleto.length || 0}/{MAX_NOMBRE_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  disabled={loading}
                  sx={{ width: '100%' }}
                  error={!!errors.nombreCompleto}
                  helperText={errors?.nombreCompleto && String(errors?.nombreCompleto?.message)}
                  variant="outlined"
                  {...register('nombreCompleto')}
                  onChange={(e) => setNombreCompleto(e.target.value)}
                  inputProps={{ maxLength: MAX_NOMBRE_LENGTH }}
                />
              </Stack>
              <Stack sx={{ width: '100%' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Nombre (buscador de imágenes)
                  <Typography variant="subtitle2">
                    {nombreBuscador.length || 0}/{MAX_NOMBRE_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  disabled={loading}
                  sx={{ width: '100%' }}
                  variant="outlined"
                  value={nombreBuscador || ''}
                  onChange={(e) => setNombreBuscador(e.target.value)}
                  inputProps={{ maxLength: MAX_NOMBRE_LENGTH }}
                />
              </Stack>
            </Stack>
            <Stack direction={isMobile ? 'column' : 'row'} sx={{ margin: '20px 0', flexGrow: 1 }} spacing="20px">
              <Stack sx={{ width: '100%' }}>
                <InputLabel>Rubro padre</InputLabel>

                <Stack sx={{ display: 'flex', flexDirection: 'column' }}>
                  <DesplegableRubrosPadres
                    rubros={rubros}
                    onChange={(e: any, value: any) => setRubroPadre(value?.codRubro || null)}
                  />
                </Stack>
              </Stack>
              <Stack sx={{ width: '100%' }}>
                <InputLabel>Precio mínimo</InputLabel>
                <TextField
                  prefix={'$'}
                  variant="outlined"
                  error={!!errors.minPrice}
                  helperText={errors?.minPrice && String(errors?.minPrice?.message)}
                  disabled={loading}
                  {...register('minPrice')}
                  placeholder="Seleccione"
                  InputLabelProps={{ shrink: false }}
                />
              </Stack>
              <Stack direction="row" sx={{ width: '100%', alignItems: 'flex-start' }} spacing="10px">
                <Stack sx={{ width: '100%' }}>
                  <InputLabel>Orden</InputLabel>
                  <TextField
                    type="number"
                    variant="outlined"
                    error={!!errors.orden}
                    helperText={errors?.orden && String(errors?.orden?.message)}
                    disabled={loading}
                    {...register('orden')}
                    placeholder="Seleccione"
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox onChange={(e) => setListable(e.target.checked)} checked={listable} disabled={loading} />
                  }
                  style={{ marginTop: '40px', whiteSpace: 'nowrap' }}
                  label={'Listable'}
                  disabled={loading}
                />
              </Stack>
            </Stack>

            <Stack direction={'column'} sx={{ marginBottom: '20px ', flexGrow: 1, width: '100%' }}>
              <InputLabel>Keywords</InputLabel>
              <KeywordsSelect setKeywords={setKeywords} />
            </Stack>
            <Stack direction={isMobile ? 'column' : 'row'} sx={{ width: '100%', margin: '20px 0' }} spacing="20px">
              <Stack sx={{ width: '100%' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '35%'
                  }}
                >
                  Color
                  <Typography variant="subtitle2">
                    {color.length || 0}/{MAX_COLOR_LENGTH}
                  </Typography>
                </InputLabel>
                <Stack sx={{ width: '100%', marginBottom: '10px' }} direction={isMobile ? 'column' : 'row'} gap="10px">
                  <Stack direction="row" gap="10px" sx={{ width: '40%', alignItems: 'center' }}>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      multiline
                      {...register('colorRubro')}
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      inputProps={{
                        maxLength: MAX_COLOR_LENGTH
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">#</InputAdornment>
                      }}
                      style={{ width: '100%' }}
                    />
                    <div
                      style={{
                        display: 'block',
                        cursor: 'pointer',
                        width: '18px',
                        height: '18px',
                        border: `1px solid black`,
                        backgroundColor: `#${color}`,
                        aspectRatio: '1/1'
                      }}
                    ></div>
                  </Stack>

                  <Stack sx={{ width: '60%', justifyContent: 'center' }}>
                    <HuePicker
                      color={`#${color}` || '#000000'}
                      onChange={(selectedColor) => setColor(selectedColor.hex.substring(1))}
                      height="18px"
                      width="100%"
                    />
                  </Stack>
                </Stack>
              </Stack>

              <Stack sx={{ width: '100%' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Slug
                  <Typography variant="subtitle2">
                    {slug.length || 0}/{MAX_SLUG_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  disabled={loading}
                  sx={{ width: '100%' }}
                  error={!!errors.slug}
                  helperText={errors?.slug && String(errors?.slug?.message)}
                  variant="outlined"
                  {...register('slug')}
                  onChange={(e) => setSlug(e.target.value)}
                  inputProps={{ maxLength: MAX_SLUG_LENGTH }}
                />
              </Stack>
            </Stack>

            <CheckboxGroup
              label="Unidad de medida"
              items={unidadesDeMedida}
              getKey={(unit) => unit.idUnidadDeMedida}
              getLabel={(unit) => `${unit.nombreSingular}/${unit.nombrePlural}`}
              isChecked={(unit) => unidadesSeleccionadas.some((u: any) => u.idUnidadDeMedida === unit.idUnidadDeMedida)}
              onChange={(unit) => handleUnidadSeleccionadaChange(unit.idUnidadDeMedida)}
              disabled={loading}
              sx={{ marginBottom: '1rem' }}
            />
            <CheckboxGroup
              label="Monedas"
              items={monedas}
              getKey={(unit) => unit.isoCode}
              getLabel={(unit) => `${unit.nombre} (${unit.isoCode}, ${unit.simbolo})`}
              isChecked={(unit) => monedasSeleccionadas.some((moneda: any) => moneda.isoCode === unit.isoCode)}
              onChange={(unit) => handleMonedaSeleccionada(unit)}
              disabled={loading}
              sx={{ marginBottom: '1rem' }}
            />
            <AtributosRubro
              atributos={gruposAtr}
              gruposSeleccionados={gruposSeleccionados}
              setGruposSeleccionados={setGruposSeleccionados}
              flagsConfig={flagsConfig}
              setFlagsConfig={setFlagsConfig}
              isMobile={isMobile}
              loading={loading}
            />
            <Stack sx={{ width: '100%', margin: '20px 0' }}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                Icono
              </InputLabel>
              <UploadImagen img={icono} setImg={setIcono} />
            </Stack>
            <Stack sx={{ width: '100%', margin: '20px 0' }}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                Imagen principal
              </InputLabel>
              <UploadImagen img={foto} setImg={setFoto} />
            </Stack>
            {!rubroPadre && (
              <Stack sx={{ width: '100%', margin: '20px 0' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Portada
                </InputLabel>
                <UploadImagen img={portada} setImg={setPortada} />
              </Stack>
            )}
            <DialogActions
              sx={{
                diplay: 'flex',
                alignItems: 'center',
                alignSelf: 'flex-end',
                width: '100%',
                padding: '22px',
                paddingTop: 0,
                gap: '8px',
                position: 'relative'
              }}
            >
              {isMobile && (
                <Button color="inherit" onClick={handleClose}>
                  Volver
                </Button>
              )}
              <Stack direction="row" spacing={2}>
                <Button variant="contained" type="submit" disabled={loading}>
                  {loading ? 'Grabando' : 'Grabar'}
                </Button>
              </Stack>
            </DialogActions>
          </DialogContent>
        </form>
      </Dialog>
    </>
  )
}

export default memo(CreaRubro)
